/// <amd-module name="Dashboards/WidgetHost/WidgetHost" />
import { resolveComponentContributionAsync, resolveWidgetContributionAsync } from "Dashboards/WidgetCatalog/WidgetContributionsHelper";
import { ContributionComponentProps, WidgetContributionRenderingProps } from "Dashboards/WidgetContracts/Internal/WidgetContributionTypes";
import { WidgetHostLoadOutcome, WidgetHostProps } from "Dashboards/WidgetContracts/Internal/WidgetHostProps";
import * as Resources from "Dashboards/WidgetHost/Resources";
import React = require("react");
import { showActionRequiredDialog } from "Reporting/Lightbox/Lightbox";
import { ActionRequiredContent } from "Reporting/Visuals/VisualMessages/ActionRequiredContent";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { DisabledWidgetHostImplementation } from "./HostImplementation/DisabledWidgetHostImplementation";
import { LegacyWidgetHostImplementation } from "./HostImplementation/LegacyWidgetHostImplementation";
import { ModernWidgetHostImplementation } from "./HostImplementation/ModernWidgetHostImplementation";
import { AccessNotSupportedMessage } from "./MessageComponents/AccessNotSupportedMessage";
import { ExtensionDisabledMessage } from "./MessageComponents/ExtensionDisabledMessage";

enum WidgetHostMode {
    loading = 0,
    renderLegacyWidgetHost = 1,
    renderModernWidgetHost = 2,
    renderAsContentBlocked = 3,
    renderAsExtensionUninstalled = 4
}

interface WidgetHostState {
    /** describes mode if widget is in a terminal (render) state  */
    terminalMode?: WidgetHostMode;

    /** Render Props on the Widget Contribution. This *should* be present on all widgets. */
    widgetContributionRenderingProps?: WidgetContributionRenderingProps;

    /** Props describing new platform render behavior on the "Satellite" Contribution. Not guaranteed to be present for all widgets. */
    widgetComponentContributionProps?: ContributionComponentProps;

    /** Indicates to fallback to legacy rendering if modern contribution was referenced but could not be loaded (notably with feature flagging). */
    fallbackToLegacyRendering?: boolean;
}

/**
 * Consumer-Facing Widget Host entry point:
 *      Responsible for choosing the "right" WidgetHost for supplied Widget.
 *      Provides error experience if Manifest is corrupt.
 *      Delegates out actual Widget Rendering. Isn't directly involved with rendering.
 *
 * Note: WidgetHost contract does not cover page-level reload. In this case, a heavy re-render of new WidgetHosts is the current model.
 */
export class WidgetHost extends VssComponent<WidgetHostProps, WidgetHostState> {
    public constructor(props: WidgetHostProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            widgetContributionRenderingProps: undefined,
            widgetComponentContributionProps: undefined,
            terminalMode: undefined
        };
    }

    public componentDidMount(): void {
        if (this.props.widgetProps.areSettingsBlockedForUser) {
            this.setState({ terminalMode: WidgetHostMode.renderAsContentBlocked });
        } else {
            this.loadCoreContribution();
        }
    }

    public render(): JSX.Element | null {
        let result = null;
        // Check if the widget type suppports configuration scenarios
        const hasConfigContribution =
            (this.props.widgetProps.configurationContributionId != null && this.props.widgetProps.configurationContributionId != "") ||
            (this.state.widgetContributionRenderingProps != null &&
                this.state.widgetContributionRenderingProps.configComponentContributionId != null &&
                this.state.widgetContributionRenderingProps.configComponentContributionId != "");
        const supportsMultipleSizes =
            this.state.widgetContributionRenderingProps != null && this.state.widgetContributionRenderingProps.supportedSizes.length > 1;
        const isWidgetConfigurable = this.props.widgetProps.isNameConfigurable || supportsMultipleSizes || hasConfigContribution;

        switch (this.determineMode()) {
            case WidgetHostMode.renderAsContentBlocked:
                result = this.renderError(WidgetHostLoadOutcome.AccessNotSupported, <AccessNotSupportedMessage />);
                break;
            case WidgetHostMode.renderAsExtensionUninstalled:
                result = this.renderError(WidgetHostLoadOutcome.ExtensionDisabled, <ExtensionDisabledMessage />);
                break;
            case WidgetHostMode.loading:
                result = this.contributionLoadingComponent();
                break;
            case WidgetHostMode.renderLegacyWidgetHost:
                result = (
                    <LegacyWidgetHostImplementation
                        isWidgetConfigurable={isWidgetConfigurable}
                        widgetContributionRenderingProps={this.state.widgetContributionRenderingProps!}
                        {...this.props}
                    />
                );
                break;

            case WidgetHostMode.renderModernWidgetHost:
                result = (
                    <ModernWidgetHostImplementation
                        {...this.props}
                        isWidgetConfigurable={isWidgetConfigurable}
                        widgetContributionRenderingProps={this.state.widgetContributionRenderingProps!}
                        widgetContributionComponentProps={this.state.widgetComponentContributionProps!}
                    />
                );
                break;
        }
        return result;
    }

    private renderError(loadOutcome: WidgetHostLoadOutcome, dialogContent: JSX.Element): JSX.Element {
        // "Disabled WidgetHost" allows for the normal host overlay behaviors, so a user isn't blocked from deleting the widget.
        return (
            <DisabledWidgetHostImplementation
                preDeterminedloadOutcome={loadOutcome}
                isWidgetConfigurable={true}
                widgetContributionRenderingProps={this.state.widgetContributionRenderingProps!}
                {...this.props}
            >
                <div className="widget-host-error">
                    <ActionRequiredContent
                        visualTitle={this.props.widgetProps.name}
                        onClick={() => {
                            showActionRequiredDialog(this.context.pageContext, Resources.DisabledWidget_DialogHeading, dialogContent);
                        }}
                    />
                </div>
            </DisabledWidgetHostImplementation>
        );
    }

    private contributionLoadingComponent = (): JSX.Element | null => {
        // Allow the widgethost to remain blank during contribution retrieval, which is comparatively short.
        // The Widget Host implementation will present a spinner after sufficient delay.
        return <div className="contribution-loading" />;
    };

    /** Pull the Widget Contribution. We branch rendering based on what is present, and the existence of New Plat contribution. */
    private async loadCoreContribution(): Promise<void> {
        try {
            const contributionprops = await resolveWidgetContributionAsync(this.context.pageContext, this.props.widgetProps.contributionId);
            const newState: WidgetHostState = {
                widgetContributionRenderingProps: contributionprops
            };
            this.setState(newState);

            if (contributionprops && contributionprops.widgetComponentContributionId) {
                const componentProps = await resolveComponentContributionAsync(
                    this.context.pageContext,
                    contributionprops.widgetComponentContributionId
                );
                if (componentProps) {
                    this.setState({
                        widgetComponentContributionProps: componentProps
                    });
                } else {
                    this.setState({ fallbackToLegacyRendering: true });
                }
            }
        } catch (e) {
            const terminalState = userHasClaim(this.context.pageContext, UserClaims.Member)
                ? WidgetHostMode.renderAsExtensionUninstalled
                : WidgetHostMode.renderAsContentBlocked;
            this.setState({ terminalMode: terminalState });
        }
    }

    private determineMode(): WidgetHostMode {
        if (this.state.terminalMode) {
            return this.state.terminalMode;
        } else {
            if (!this.state.widgetContributionRenderingProps) {
                return WidgetHostMode.loading;
            } else if (this.state.widgetContributionRenderingProps.widgetComponentContributionId && !this.state.fallbackToLegacyRendering) {
                if (!this.state.widgetComponentContributionProps) {
                    return WidgetHostMode.loading;
                } else {
                    return WidgetHostMode.renderModernWidgetHost;
                }
            } else {
                return WidgetHostMode.renderLegacyWidgetHost;
            }
        }
    }
}
